import { useCallback, useRef, useState } from 'react';
import { FileUp, Loader2, AlertCircle } from 'lucide-react';
import { onboardingStepShellClass } from '../Utils/onboardingLayout';

interface ImportStepProps {
  parsing: boolean;
  error: string | null;
  onParseFile: (file: File) => Promise<void>;
  onParseText: (text: string) => Promise<void>;
  onSkip: () => void;
}

export const ImportStep = ({
  parsing,
  error,
  onParseFile,
  onParseText,
  onSkip
}: ImportStepProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [pasteText, setPasteText] = useState('');

  const handleFiles = useCallback(
    async (list: FileList | null) => {
      const file = list?.[0];
      if (!file || parsing) return;
      await onParseFile(file);
    },
    [onParseFile, parsing]
  );

  const handleExtractText = useCallback(async () => {
    const trimmed = pasteText.trim();
    if (!trimmed || parsing) return;
    await onParseText(trimmed);
  }, [onParseText, parsing, pasteText]);

  const canExtractText = pasteText.trim().length > 0 && !parsing;

  return (
    <div className={onboardingStepShellClass}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-semibold tracking-tight text-[#171717]">
            Import inventory
          </h2>
          <p className="mt-1 text-sm text-[#6f747c]">
            Paste a list or notes below — or upload a spreadsheet. When import
            finishes, you&apos;ll continue to Items automatically.
          </p>
        </div>
        <button
          type="button"
          disabled={parsing}
          onClick={() => inputRef.current?.click()}
          className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-[#e7e9ee] bg-white px-3 text-sm font-semibold text-[#171717] transition hover:bg-[#f7f8fa] disabled:cursor-not-allowed disabled:opacity-40"
        >
          {parsing ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-[#087f8c]" />
          ) : (
            <FileUp className="h-3.5 w-3.5 text-[#087f8c]" />
          )}
          Upload file
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          disabled={parsing}
          onChange={e => {
            void handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      <div className="rounded-lg border border-[#e7e9ee] bg-white p-5">
        <label
          htmlFor="inventory-import-paste"
          className="text-sm font-medium text-[#171717]"
        >
          Paste inventory text
        </label>
        <p className="mt-0.5 text-xs text-[#6f747c]">
          Lists, invoice notes, or email — CSV/Excel via Upload file (max 10 MB)
        </p>
        <textarea
          id="inventory-import-paste"
          value={pasteText}
          onChange={e => setPasteText(e.target.value)}
          disabled={parsing}
          placeholder={`Roma Tomatoes — 2 cs\nOlive Oil 6 bottles\nChicken Breast 40 lb`}
          className="mt-3 min-h-[min(52vh,420px)] w-full resize-y rounded-md border border-[#e7e9ee] bg-white px-3 py-2.5 text-sm text-[#171717] outline-none transition placeholder:text-[#a0a4ab] focus:border-[#087f8c] focus:ring-2 focus:ring-[#087f8c]/15 disabled:opacity-60"
        />
        <button
          type="button"
          disabled={!canExtractText}
          onClick={() => void handleExtractText()}
          className="mt-3 h-9 rounded-md bg-[#087f8c] px-3 text-sm font-semibold text-white transition hover:bg-[#066873] disabled:cursor-not-allowed disabled:opacity-40"
        >
          {parsing ? (
            <span className="inline-flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Importing…
            </span>
          ) : (
            'Import'
          )}
        </button>
      </div>

      {error ? (
        <div className="flex items-start gap-2 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      ) : null}

      <div className="space-y-2 pt-1">
        <p className="text-sm text-[#6f747c]">
          Don&apos;t want to paste or upload? Skip this and add items yourself on
          the next screen.
        </p>
        <button
          type="button"
          disabled={parsing}
          onClick={onSkip}
          className="rounded-md border border-[#e7e9ee] bg-white px-4 py-2 text-sm font-semibold text-[#171717] transition hover:bg-[#f7f8fa] disabled:cursor-not-allowed disabled:opacity-40"
        >
          Skip import — I&apos;ll add items myself
        </button>
      </div>
    </div>
  );
};
